import {
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  flowMax,
} from 'ad-hok'
import cx from 'classnames'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import { isLoginError } from 'shared/src/api/types'
import { addNavigate } from 'shared/src/utils/addNavigate'
import { addAuthContext } from 'shared/src/utils/auth'
import { isValidPassword } from 'shared/src/utils/validators'

import { addToastContext } from 'components/toastContext'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { Button } from '../Button'
import { TextFieldNew } from '../TextField'
import { SANS_SERIF_FONT_FAMILY, SERIF_FONT_FAMILY } from '../styles'

const useStyles = createUseStyles({
  title: {
    fontFamily: SERIF_FONT_FAMILY,
    fontSize: 32,
    fontWeight: 600,
    lineHeight: '140%',
    letterSpacing: 0.01,
    marginTop: 16,
    marginBottom: 12,
    fontStyle: 'italic',
  },
  subtitle: {
    fontFamily: SANS_SERIF_FONT_FAMILY,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '187%',
    color: 'rgba(144, 115, 79, 0.8)',
    marginLeft: 30,
    marginBottom: 40,
  },
  passwordRequirements: {
    fontFamily: SANS_SERIF_FONT_FAMILY,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '160%',
    letterSpacing: 0.2,
    marginTop: 4,
    marginBottom: 18,
    color: 'rgba(54, 52, 50, 0.8)',
  },
  passwordRequirementsError: {
    color: '#F57561',
  },
  authErrorText: {
    fontFamily: SANS_SERIF_FONT_FAMILY,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '160%',
    letterSpacing: 0.2,
    marginTop: 4,
    marginBottom: 18,
    color: '#F57561',
  },
})

interface Props {
  email: string
  code?: string
  isUpdate?: boolean
}

export const SetPassword: FC<Props> = flowMax(
  addDisplayName('SetPassword'),
  addProps(() => ({
    classes: useStyles(),
  })),
  addState('password', 'onChangePassword', ''),
  addApi,
  addNavigate,
  addState('authError', 'setAuthError', false),
  addAuthContext,
  addState('errorMessage', 'setErrorMessage', ''),
  addTranslationHelpers,
  addToastContext,
  addAuthContext,
  addHandlers({
    onClick:
      ({
        api,
        email,
        password,
        code,
        navigate,
        setErrorMessage,
        showSuccessToast,
        setAuthError,
        logIn,
        t,
      }) =>
      async () => {
        try {
          const response = await api.auth.logIn({
            email,
            password,
            resetPasswordToken: code,
          })

          if (isLoginError(response)) {
            setAuthError(true)
            setErrorMessage(
              t(
                response.error === 'invalidToken'
                  ? 'auth.updatePassword.invalidToken'
                  : 'auth.genericError'
              )
            )
          } else {
            logIn()
            navigate('/')
            showSuccessToast({
              title: t('auth.updatePassword.passwordUpdated'),
              details: '',
            })
          }
        } catch (error) {
          setErrorMessage(t('auth.genericError'))
        }
      },
  }),
  addProps(
    ({ password, t }) => ({
      error:
        password.length === 0 || isValidPassword(password)
          ? undefined
          : t('auth.register.invalidPassword'),
    }),
    ['password', 't']
  ),
  addHandlers({
    onKeyPress:
      ({ onClick, password, error }) =>
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && !(password === '' || !!error)) {
          onClick()
        }
      },
  }),
  ({
    isUpdate,
    password,
    onChangePassword,
    onClick,
    onKeyPress,
    authError,
    errorMessage,
    error,
    classes,
    t,
  }) => (
    <div>
      <h1 className={classes.title}>
        {t(
          isUpdate
            ? 'auth.updatePassword.title'
            : 'auth.register.createPasswordTitle'
        )}
      </h1>
      <p className={classes.subtitle}>
        {t('auth.register.createPasswordSubtitle')}
      </p>
      {authError && (
        <div className={classes.authErrorText}>
          {t('auth.updatePassword.error')}
        </div>
      )}
      <TextFieldNew
        id="password"
        type="password"
        label={t('auth.fields.password')}
        value={password ?? ''}
        onChange={(password) => onChangePassword(password)}
        showClearInput={false}
        error={error}
        showErrorMessage={false}
        autoComplete={false}
        onKeyPress={onKeyPress}
      />
      <div
        className={cx(
          classes.passwordRequirements,
          (errorMessage !== '' || error) && classes.passwordRequirementsError
        )}
      >
        {errorMessage !== '' ? errorMessage : t('auth.passwordRequirements')}
      </div>
      <Button
        text={t(
          isUpdate
            ? 'auth.updatePassword.button'
            : 'auth.register.setPasswordButton'
        )}
        type="fullWidth"
        onClick={onClick}
        disabled={password === '' || !!error}
      />
    </div>
  )
)
